import { Checkbox, Icon, Text, Button, Input, Flex, Pill } from '@/_components';
import styles from './Document.module.css';
import { updateDescription } from '@/_api/admin-documents';
import { useContext, useState, useEffect, FC } from 'react';
import { DocumentProps } from './Document.types';
import { UserContext } from '@/_contexts/UserProvider';

export const Document: FC<DocumentProps> = ({
  id,
  companyId,
  description,
  name,
  isChecked,
  isExported,
  needsDescription,
  onToggleDocument,
  onDescriptionUpdated,
  dragListeners,
}) => {
  const { token } = useContext(UserContext);

  const [editing, setEditing] = useState<boolean>(false);
  const [newDescription, setNewDescription] = useState<string>(description);
  const [shouldPulse, setShouldPulse] = useState<boolean | undefined>(
    needsDescription,
  );

  const saveDescription = (
    id: number,
    companyId: number,
    newDescription: string,
  ) => {
    updateDescription({
      id,
      companyId,
      description: newDescription,
      token,
    }).then(() => {
      onDescriptionUpdated(id, newDescription);
    });

    setEditing(false);
  };

  const onToggle = () => {
    onToggleDocument(id);
  };

  const onDescriptionChange = (event: any) => {
    setNewDescription(event.target.value);
    setShouldPulse(false);
  };

  const onCancel = () => {
    setNewDescription(description);
    setEditing(false);
  };

  useEffect(() => {
    setNewDescription(description);
  }, [description]);

  return (
    <Flex isBetween isYCentered gap={4} className={styles.container}>
      <Flex isYCentered gap={4} className={styles.nameAndDescription}>
        <div {...dragListeners}>
          <Icon color="accent" name="dots-six-vertical" size={16} />
        </div>
        <Checkbox
          key={id}
          label="Select Document"
          isLabelHidden={true}
          isChecked={isChecked}
          onChange={onToggle}
        />
        {editing ? (
          <Input
            label="Edit document description"
            isLabelHidden={true}
            value={newDescription}
            name="description"
            onChange={onDescriptionChange}
          />
        ) : (
          <>
            <Text noWrap={true} isEllipsis={true}>
              {name}
            </Text>
            <Pill
              intent="info"
              label={description}
              isRound={true}
              isEllipsis={true}
            />
            {isExported && (
              <Pill
                intent="success"
                iconLeft="check"
                label="Exported"
                isDark={false}
                isRound={true}
              />
            )}
          </>
        )}
      </Flex>
      <Flex isYCentered gap={4}>
        {editing ? (
          <>
            <Button
              intent="secondary"
              size="tiny"
              value="Save"
              isIconOnly={true}
              withTooltip={true}
              iconLeft="check"
              onClick={() => saveDescription(id, companyId, newDescription)}
            />
            <Button
              intent="danger-secondary"
              size="tiny"
              value="Cancel"
              isIconOnly={true}
              withTooltip={true}
              iconLeft="x"
              onClick={onCancel}
            />
          </>
        ) : (
          <Button
            intent="ghost"
            size="tiny"
            value="Edit Description"
            isIconOnly={true}
            isPulsing={shouldPulse}
            withTooltip={true}
            iconLeft="pencil"
            onClick={() => setEditing(true)}
          />
        )}
      </Flex>
    </Flex>
  );
};
